/**
 * Deterministische "past-erbij"-logica voor upsells (firebase-vrij, net als relevance.ts).
 *
 * Voor beslag is de vraag "past dit technisch bij elkaar?" belangrijker dan "kochten anderen dit
 * ook?" — een chroom scharnier voor 8-10 mm glas hoort bij een chroom klem voor 8-10 mm glas.
 * Deze regels werken vanaf dag 1 (geen verkoopdata nodig) en vormen de basislaag onder de
 * co-aankoop-/co-winkelwagendata: zodra die data er is, wint die (zie relevance.ts + ProductPage).
 */

/** Groep → groepen die je er functioneel vaak bij nodig hebt (binnen dezelfde categorie). */
const COMPLEMENT_GROUPS: Record<string, string[]> = {
  // Douche & Bad
  Scharnieren: ["Glasklemmen", "Afdichtprofielen", "Stabilisatie", "Knoppen & Grepen"],
  Glasklemmen: ["Scharnieren", "Afdichtprofielen", "Stabilisatie", "Profielen", "Leuningen", "Punthouders"],
  Afdichtprofielen: ["Scharnieren", "Glasklemmen", "Stabilisatie"],
  Stabilisatie: ["Scharnieren", "Glasklemmen", "Afdichtprofielen", "Knoppen & Grepen"],
  "Knoppen & Grepen": ["Scharnieren", "Afdichtprofielen", "Stabilisatie"],
  "Schuif- & inloopdouche": ["Stabilisatie", "Afdichtprofielen", "Knoppen & Grepen", "Glasklemmen"],
  Sauna: ["Knoppen & Grepen", "Afdichtprofielen"],
  Handdoekstang: ["Scharnieren", "Knoppen & Grepen"],
  // Balustrade
  Punthouders: ["Profielen", "Leuningen", "Glasklemmen"],
  Leuningen: ["Profielen", "Glasklemmen", "Punthouders"],
  "Frans balkon": ["Glasklemmen", "Profielen", "Leuningen"],
  Profielen: ["Leuningen", "Glasklemmen", "Punthouders", "Afdichtprofielen"],
  // Interieurbeglazing
  Aanslagdeuren: ["Grepen", "Deurkader", "Profielen"],
  Pendeldeuren: ["Grepen", "Profielen", "Deurkader"],
  Schuifdeuren: ["Grepen", "Profielen"],
  Grepen: ["Aanslagdeuren", "Pendeldeuren", "Schuifdeuren", "Deurkader"],
  Deurkader: ["Profielen", "Grepen"],
  Overkappingssystemen: ["Profielen", "Glasklemmen"],
};

/** Vergaarbak-groepen: te breed om als zinnige upsell te dienen (zie compatibilityScore). */
const CATCH_ALL_GROUPS = new Set(["Overige", "Overig", "Diversen"]);

// Dimensies ("20x20mm", "79x37mm") zijn géén glasdikte → eerst weghalen.
const DIM = /\d+\s*[x×]\s*\d+\s*mm/gi;
// "6-8mm", "10/12mm", "6 & 8mm"
const RANGE = /\b(\d{1,2})\s*(?:[-–]|&|\/|\s+en\s+)\s*(\d{1,2})\s*mm\b/i;
// "10mm glas"
const GLASS = /\b(\d{1,2})\s*mm\s*glas\b/i;
// "glasdikte 10 mm", "t.b.v. 8 mm", "voor 8-10 mm"
const CUE = /(?:glasdikte|t\.?b\.?v\.?|voor|geschikt voor)[^0-9]{0,18}(\d{1,2})\s*(?:[-–]|&|\/)?\s*(\d{1,2})?\s*mm/i;

/** Glasdikte(bereik) in mm uit een artikelnaam, of null als het er niet in staat. */
export function glassThickness(name: string): [number, number] | null {
  const s = name.replace(DIM, " ");
  for (const rx of [RANGE, GLASS, CUE]) {
    const m = rx.exec(s);
    if (!m) continue;
    const nums = m.slice(1).filter(Boolean).map(Number).filter((n) => n >= 3 && n <= 25);
    if (nums.length) return [Math.min(...nums), Math.max(...nums)];
  }
  return null;
}

/** Overlappen twee glasdikte-bereiken? Onbekend (null) telt als "geen bezwaar". */
function thicknessFits(a: [number, number] | null, b: [number, number] | null): boolean | null {
  if (!a || !b) return null;
  return a[0] <= b[1] && b[0] <= a[1];
}

/**
 * Systeemherkenning: welke onderdelen horen bij welk profiel/beslagsysteem?
 *
 * Aanleiding (09-09-2026): een klant belde over klikprofielen. Er zijn er twee — 70KLIK en
 * 13DFKLIK — en die hebben elk hun eigen rubbers, eindstukken en hoekverbinders. Op de site is
 * niet te zien welk profiel welk is: ze heten allebei "Klikprofiel incl. kliklat …" en verschillen
 * alleen in hun maten (25x25x25 tegen 30x25x30). De onderdelen zéggen wel bij welk systeem ze
 * horen ("Klemrubber t.b.v. klikprofiel 13DFKLIK"), maar het profiel zelf zegt niets.
 *
 * Twee bronnen, met verschillend gewicht:
 *  - het LEVERANCIERSNUMMER van het artikel zelf (70KLIK-12 → 70KLIK). Een hint: bruikbaar om te
 *    kóppelen, te zwak om iets op uit te sluiten — 70RUBBERFIX hoort bij 70KLIK maar zegt dat
 *    nergens.
 *  - een expliciete vermelding in de NAAM ("t.b.v. 13DFKLIK & 13DF02"). Dat is een uitspraak van
 *    de fabrikant; daar mag je wél iets op uitsluiten.
 *
 * Een puur op nummers gebaseerde indeling is geprobeerd en verworpen: die knipt 70KLIK doormidden
 * (de eindstukken heten 70EINDST) en maakt van Timmers' verzamelprefix "50" families van 83
 * ongerelateerde artikelen. Het nummer is een hint, geen sleutel.
 */

/** Codevorm van een systeem: cijfers gevolgd door letters — 70KLIK, 13DFKLIK, 70U25, 13DF02. */
const SYSTEEM_CODE = /\b(\d{2,3}[A-Z]{1,}[A-Z0-9]{0,8})\b/g;
/**
 * Seriecode met letters vóór: TL-6020, TL-3010, V-504, BX-5000. Zo noemt OnLevel zijn
 * balustradeprofielen, en zo noemen slot- en schuifdeurfabrikanten hun series. De oude herkenning
 * las alleen cijfers-vóór (70KLIK) en zag daardoor de hele balustradelijn niet.
 */
const SERIE_CODE = /\b([A-Z]{1,3})-(\d{2,5})\b/g;
/** Materiaal- en normaanduidingen met dezelfde vorm die géén systeem zijn (RVS-316). */
const GEEN_SERIE = new Set(["RVS", "RAL", "DIN", "EN", "NEN", "ISO", "AISI", "M"]);
/** Eenheden die op een systeemcode lijken maar het niet zijn: 310ML, 80MU, 120KG, 135CM, 10MM. */
const EENHEID = /^\d+(ML|MU|KG|CM|MM|NM|GR|L|W|V)$/;
/** De zinsnede waarin een artikel zegt waar het bij hoort. */
const TBV = /(?:t\.?\s?b\.?\s?v\.?|geschikt voor|passend bij|past op|voor de)\s+([^,;.]{0,80})/i;
/**
 * Verwijzing naar het ARTIKELNUMMER van een ander product: "tbv 661501720/721/722", "t.b.v.
 * 011446 slot". Niet als het getal deel is van een code (TL-6020) of als er een eenheid achter
 * staat — "inloopdouche 3000mm" is een maat, en die koppelde bij een ruimere regel aan 102 artikelen.
 */
const NUMMER_REF = /(?<![\w,\-])(\d{4,9})((?:\/\d{2,9})*)(?![\d,]|\s*(?:mm|cm|kg|kn|m\b|x))/gi;
/** Voorvoegsel voor een artikelnummer-verwijzing, zodat die nooit botst met een echte systeemcode. */
const NR = "#";

const schoon = (code: string): string | null => (EENHEID.test(code) ? null : code);

/** Is dit een verwijzing naar een artikelnummer (intern), en dus niets om aan de klant te tonen? */
export function isNummerVerwijzing(code: string): boolean {
  return code.startsWith(NR);
}

/** Letter-seriecodes uit een stuk tekst (TL-6020, V-504), zonder materiaalaanduidingen. */
function serieCodes(tekst: string): string[] {
  const uit = new Set<string>();
  for (const m of tekst.toUpperCase().matchAll(SERIE_CODE)) {
    if (!GEEN_SERIE.has(m[1]!)) uit.add(`${m[1]}-${m[2]}`);
  }
  return [...uit];
}

/** "661501720/721/722" → #661501720, #661501721, #661501722 · "011445/011448" → #011445, #011448. */
function nummerVerwijzingen(tekst: string): string[] {
  const uit = new Set<string>();
  for (const m of tekst.matchAll(NUMMER_REF)) {
    const eerste = m[1]!;
    uit.add(NR + eerste);
    for (const deel of (m[2] ?? "").split("/").filter(Boolean)) {
      const vol = deel.length >= eerste.length ? deel : eerste.slice(0, eerste.length - deel.length) + deel;
      uit.add(NR + vol);
    }
  }
  return [...uit];
}

/**
 * Systeem waar dit artikel ZELF deel van uitmaakt, uit het leveranciersnummer (70KLIK-12 →
 * 70KLIK). Zwakke bron: goed genoeg om te koppelen, niet om uit te sluiten.
 */
export function eigenSysteem(articleNumber: string | undefined): string | null {
  const kop = (articleNumber ?? "").toUpperCase().split(/[-.\/ ]/)[0] ?? "";
  if (kop.length < 4 || /^\d+$/.test(kop)) return null; // "50" is Timmers' verzamelprefix
  return schoon(kop);
}

/** Kop van een puur numeriek artikelnummer als landingsplek voor een verwijzing (011446-30 → #011446). */
function eigenNummer(articleNumber: string | undefined): string | null {
  const kop = (articleNumber ?? "").split(/[-.\/ ]/)[0] ?? "";
  return /^\d{4,9}$/.test(kop) ? NR + kop : null;
}

/**
 * Systemen waarvoor dit artikel volgens zijn NAAM geschikt is ("t.b.v. …"). Sterke bron: hierop
 * mag je een combinatie afwijzen. Bevat ook artikelnummer-verwijzingen (met #-voorvoegsel).
 */
export function passendeSystemen(name: string): string[] {
  const m = TBV.exec(name ?? "");
  if (!m?.[1]) return [];
  const uit = new Set<string>();
  for (const t of m[1].toUpperCase().matchAll(SYSTEEM_CODE)) {
    const c = schoon(t[1]!);
    if (c) uit.add(c);
  }
  serieCodes(m[1]).forEach((c) => uit.add(c));
  nummerVerwijzingen(m[1]).forEach((c) => uit.add(c));
  return [...uit];
}

/**
 * Seriecodes die het artikel ZELF in zijn naam draagt, buiten de "t.b.v."-zinsnede:
 * "Glasprofiel TL-6020 Topmontage" draagt TL-6020; "Afstandhouder t.b.v. TL-6020" verwijst ernaar.
 */
export function gedragenSystemen(name: string): string[] {
  const tekst = name ?? "";
  const m = TBV.exec(tekst);
  const zonderVerwijzing = m ? tekst.slice(0, m.index) + tekst.slice(m.index + m[0].length) : tekst;
  return serieCodes(zonderVerwijzing);
}

/**
 * Horen twee codes bij elkaar? Gelijk, óf een serieverwijzing die de kop is van een modelcode.
 *
 * OnLevel noemt de serie "TL-60" en de modellen "TL-6020" (topmontage) en "TL-6021" (zijmontage).
 * Een onderdeel "t.b.v. TL-60" past op beide; een onderdeel "t.b.v. TL-6020" alleen op de
 * topmontage. Precies zoals de fabrikant het in de artikelnamen schrijft (nagekeken 11-09-2026).
 */
function codesPassen(a: string, b: string): boolean {
  if (a === b) return true;
  const serie = (c: string) => /^([A-Z]{1,3})-(\d{2})$/.exec(c);
  const model = (c: string) => /^([A-Z]{1,3})-(\d{4,5})$/.exec(c);
  const s1 = serie(a), m1 = model(b);
  if (s1 && m1 && s1[1] === m1[1] && m1[2]!.startsWith(s1[2]!)) return true;
  const s2 = serie(b), m2 = model(a);
  return !!(s2 && m2 && s2[1] === m2[1] && m2[2]!.startsWith(s2[2]!));
}

function overlapt(a: Iterable<string>, b: Set<string>): boolean {
  for (const x of a) for (const y of b) if (codesPassen(x, y)) return true;
  return false;
}

interface SysteemArtikel {
  name: string;
  articleNumber?: string;
  /** Handmatig gezet in Productbeheer; wint van alles wat we afleiden. */
  systeem?: string;
}

/**
 * Het systeem van één artikel, zoals je het aan de klant toont en waarop je filtert.
 * Handmatig (Productbeheer) wint, dan een code in de eigen naam, het leveranciersnummer, en een
 * "t.b.v."-vermelding. Eén bron voor de productpagina, de categorielijst, de zoeker en de
 * winkelwagen — anders leidt elke plek zijn eigen systeem af en wijken ze op den duur af.
 */
export function systeemVan(
  a: { slug?: string; name: string; articleNumber?: string },
  ov?: { systeemPerSlug?: Record<string, string> } | null,
): string | null {
  const hand = a.slug ? ov?.systeemPerSlug?.[a.slug]?.trim() : "";
  if (hand) return hand.toUpperCase();
  return (
    gedragenSystemen(a.name)[0] ??
    eigenSysteem(a.articleNumber) ??
    passendeSystemen(a.name).find((c) => !isNummerVerwijzing(c)) ??
    null
  );
}

/**
 * Hoort dit artikel bij systeem `code`? Ruimer dan `systeemVan`: een onderdeel dat "t.b.v.
 * 70KLIK/70U25" heet hoort bij béíde systemen, en een onderdeel "t.b.v. TL-60" bij elk TL-60-model.
 * Voor filteren moet je ze allemaal meenemen, anders mist de lijst juist de onderdelen die je zocht.
 */
export function hoortBijSysteem(
  a: { slug?: string; name: string; articleNumber?: string },
  code: string,
  ov?: { systeemPerSlug?: Record<string, string> } | null,
): boolean {
  const gezocht = code.trim().toUpperCase();
  if (!gezocht) return true;
  const hand = a.slug ? ov?.systeemPerSlug?.[a.slug]?.trim().toUpperCase() : "";
  const eigen = [
    hand,
    eigenSysteem(a.articleNumber),
    ...gedragenSystemen(a.name),
    ...passendeSystemen(a.name).filter((c) => !isNummerVerwijzing(c)),
  ].filter((c): c is string => !!c);
  return eigen.some((c) => codesPassen(c, gezocht));
}

/** Alle systeemcodes van een artikel + of er een HARDE (expliciete) bij zit. */
function systemenVan(a: SysteemArtikel): { codes: Set<string>; koppel: Set<string>; expliciet: boolean } {
  const codes = new Set<string>();
  const hand = a.systeem?.trim().toUpperCase();
  if (hand) codes.add(hand);
  const uitNaam = passendeSystemen(a.name);
  uitNaam.forEach((c) => codes.add(c));
  const gedragen = gedragenSystemen(a.name);
  gedragen.forEach((c) => codes.add(c));
  const eigen = eigenSysteem(a.articleNumber);
  if (eigen) codes.add(eigen);
  // Het eigen artikelnummer mag een verwijzing laten LANDEN ("tbv 661501720" → het scharnier met dat
  // nummer), maar telt niet mee voor de vraag of dit artikel überhaupt een systeem heeft. Anders had
  // elk artikel er een, en werd elke combinatie met een expliciet onderdeel ineens "past niet".
  const koppel = new Set(codes);
  const nr = eigenNummer(a.articleNumber);
  if (nr) koppel.add(nr);
  return { codes, koppel, expliciet: !!hand || uitNaam.length > 0 || gedragen.length > 0 };
}

/**
 * Hoort `a` bij hetzelfde systeem als `ref`?
 * true = aantoonbaar wel · false = aantoonbaar niet · null = onbekend (geen oordeel).
 *
 * `false` komt er alleen uit als MINSTENS ÉÉN kant expliciet een systeem noemt (in de naam of via
 * Productbeheer) en de ander daar niet bij hoort. Twee kanten eisen kan niet: een profiel noemt
 * zijn systeem soms alleen in het leveranciersnummer — "Klikprofiel incl. kliklat 25x25x25" zegt er
 * niets over. Maar zonder énige expliciete uitspraak oordelen we niet, anders filteren we
 * onderdelen weg die wél passen maar dat nergens zeggen (70RUBBERFIX).
 */
export function systeemFit(a: SysteemArtikel, ref: SysteemArtikel): boolean | null {
  const A = systemenVan(a);
  const R = systemenVan(ref);
  if (overlapt(A.koppel, R.koppel)) return true;
  if (!A.codes.size || !R.codes.size) return null;
  return A.expliciet || R.expliciet ? false : null;
}

export interface ComplementInput {
  slug: string;
  name: string;
  groep: string;
  subgroep?: string;
  merk?: string;
  color?: string;
  category?: string;
  /** Alleen gezet als het artikel elders thuishoort en hier meelift (multi-rubriek). */
  homeCategory?: string;
  /** Leveranciersnummer — alleen intern gebruikt om het systeem af te leiden. */
  articleNumber?: string;
  /** Systeem uit Productbeheer (handmatig); wint van wat we afleiden. */
  systeem?: string;
}

/**
 * Hoe goed past `a` als AANVULLING bij `ref` (ander product dat je erbij nodig hebt).
 * Positief = past; negatief = past aantoonbaar niet (bv. glasdikte sluit elkaar uit).
 * De schaal is bewust vergelijkbaar met relevanceScore, zodat een heel populair artikel nog
 * kan doorbreken maar een technisch onmogelijke combinatie nooit bovenaan komt.
 */
export function compatibilityScore(a: ComplementInput, ref: ComplementInput): number {
  let s = 0;
  if (COMPLEMENT_GROUPS[ref.groep]?.includes(a.groep)) s += 6000; // functioneel complement
  // "Overige" is een vergaarbak (honderden ongerelateerde artikelen). Zonder deze rem liften
  // die mee op een toevallige kleurmatch en verdringen ze de échte complementen.
  else if (CATCH_ALL_GROUPS.has(a.groep)) s -= 4000;
  // Kruislings gekoppelde artikelen (thuis in een ándere afdeling) dragen de groep van de
  // rubriek waarin ze meeliften — een vitrineslot komt zo als "Scharnieren" in Douche & Bad
  // terecht. Ze mogen meedoen, maar nooit vóór het echte assortiment van deze afdeling.
  if (a.homeCategory && a.homeCategory !== (ref.homeCategory ?? ref.category)) s -= 5000;
  if (a.merk && a.merk === ref.merk) s += 2000; // zelfde systeem (Marine, Estlegno, …)
  if (a.color && a.color === ref.color) s += 2000; // zelfde afwerking (chroom bij chroom)
  const fits = thicknessFits(glassThickness(a.name), glassThickness(ref.name));
  if (fits === true) s += 4000;
  else if (fits === false) s -= 8000; // past aantoonbaar niet op deze glasdikte
  // Zelfde profiel-/beslagsysteem: het sterkste signaal dat er is. Een klemrubber "t.b.v.
  // 13DFKLIK" hoort bij het 13DFKLIK-profiel en bij géén ander — voorstellen dat het wél past
  // kost een retour.
  const sys = systeemFit(a, ref);
  if (sys === true) s += 7000;
  else if (sys === false) s -= 9000;
  return s;
}

/**
 * Hoe goed is `a` een ALTERNATIEF voor `ref` (vergelijkbaar product)? Zelfde (sub)groep,
 * systeem en afwerking eerst; glasdikte moet passen.
 */
export function similarityScore(a: ComplementInput, ref: ComplementInput): number {
  let s = 0;
  if (a.subgroep && a.subgroep === ref.subgroep) s += 3000;
  if (a.merk && a.merk === ref.merk) s += 2000;
  if (a.color && a.color === ref.color) s += 2000;
  const fits = thicknessFits(glassThickness(a.name), glassThickness(ref.name));
  if (fits === true) s += 3000;
  else if (fits === false) s -= 6000;
  return s;
}
